import api from './api'

// ─── Editable entity types ────────────────────────────────────────────
//
// The 10 contextEngine entities surfaced on the client Knowledge page.
// Order here matches sidebar tab order: Offer → Audience → Brand voice
// → Voice of customer → Strategy.

export type KnowledgeType =
  // The business brief — a JSON OBJECT (not a list): what this business is,
  // split out of the merchant's site/description. Editable since 2026-08-17.
  | 'business_truth.business_brief'
  | 'activation.value_props'            // JSON array
  | 'activation.differentiators'        // JSON array
  | 'activation.audience_segments'      // JSON array
  | 'activation.objections'             // JSON array
  | 'customer_evidence.phrases'         // JSON array
  | 'activation.content_pillars'        // JSON array
  | 'business_truth.constraints'        // markdown bullets (one rule per line)
  // AI settings surface (LP mode) — answers to the original onboarding
  // questions, edited as free-form text. business_truth/marketing_identity
  // entities store a single string blob; activation.extra_context is a
  // free-form merchant-supplied addendum.
  | 'business_truth.audience'
  | 'business_truth.offer'
  | 'business_truth.positioning'
  | 'marketing_identity.brand_voice'
  | 'marketing_identity.voice_profiles'
  | 'activation.extra_context'
  // The four sections the overview listed with no page behind them — pages
  // added 2026-08-18 so the Brain matches the overview. goals + branding_kit
  // are markdown blobs; funnel_shape is one choice token; lead-magnet
  // concepts are id-LESS cards (the array saves whole, no id-merge).
  | 'business_truth.goals'
  | 'business_truth.funnel_shape'
  | 'marketing_identity.branding_kit'
  | 'activation.lead_magnet_concepts'
  // Structured, engine-owned libraries used as Campaign setup suggestions.
  // They are readable from the knowledge endpoint but intentionally cannot
  // be written through saveKnowledge.
  | 'business_truth.targets'
  | 'activation.cta_library'
  // The engine-generated content plan — now hand-editable (topics + nested
  // sub-angles). A deliberate "regenerate strategy" re-derives it; day-to-day
  // edits stick (writeContext stamps source:'user', which staleness trusts).
  | 'activation.topic_roster'

export interface KnowledgeEntry {
  contextId?: number
  currentVersionId?: number | null
  content: unknown          // string for markdown, array for JSON entities
  format: 'text' | 'json'
  confidence: number | null
  source: string
  versionCreatedAt: string | null
}

export type KnowledgePayload = Partial<Record<KnowledgeType, KnowledgeEntry>>

// ─── JSON entity item shapes ──────────────────────────────────────────
// Mirror the backend generator schemas exactly. The UI may render only a
// subset of fields; the controller id-merges on save so hidden fields
// (proofSource, whatTheyWantToHear, evidence, etc.) are preserved.

export interface Phrase {
  id: string
  phrase: string
  intent: 'outcome' | 'problem' | 'objection' | 'desire' | 'inferred'
  source: string
}

export interface AudienceSegment {
  id: string
  label: string
  priority: 'primary' | 'secondary' | 'tertiary'
  profile: string
  mindset: string
  pains: string[]
  whatTheyWantToHear: string
}

export interface Differentiator {
  id: string
  claim: string
  againstWhat: string
  proofSource: string
}

export interface Objection {
  id: string
  objection: string
  counter: string
  evidence: string
}

export interface ContentPillar {
  id: string
  name: string
  description: string
  icon?: string   // Iconify id (e.g. 'lucide:scissors'), assigned at AI generation
}

export interface ValueProposition {
  id: string
  headline: string
  statement: string
  proofPoint: string
}

/** A person or company identity that may speak for the business. Profiles
 * inherit the shared Brand voice; `guidance` records only the differences. */
export interface VoiceProfile {
  id: string
  name: string
  publisherType: 'founder' | 'expert' | 'company' | 'employee'
  roleAndAuthority?: string
  guidance?: string
  channelNotes?: string
  status: 'active' | 'inactive'
}

// Lead-magnet concept cards — the ONE card list with no `id` field (the
// 2026-05-28 flatten kept the generator's raw shape). The array is saved
// whole; edits target items by index, not id.
export interface LeadMagnetConcept {
  title?: string
  format?: 'checklist' | 'guide' | 'template' | 'toolkit' | 'assessment'
  problemSolved?: string
  outline?: string[]
  welcomeSequenceConnection?: string
}

// ─── Topic roster (read-only) ─────────────────────────────────────────
// The engine's content plan: topics (each anchored to a pillar) carrying
// sub-angles. Every sub-angle's persuasion spine is the old-way → new-way
// "shift". Rendered for viewing only — see KnowledgeType note above.

export interface TopicSubAngle {
  id: string
  label: string
  shift?: { oldWay: string; newWay: string; gapMechanismSuggestion?: string }
  structuralShape?: string | null
  angleType?: string | null
  funnelStage?: string | null
}

export interface TopicRosterItem {
  id: string
  name: string
  pillarId?: string
  icon?: string   // Iconify id, assigned at AI generation
  subAngles?: TopicSubAngle[]
}

export interface BusinessTarget {
  metric: string
  baseline: number | null
  target: number
  period: string
}

export interface CtaLibraryItem {
  id: string
  text: string
  funnelStage: 'awareness' | 'consideration' | 'decision' | 'retention'
  channel: 'blog' | 'social' | 'email' | 'ads' | 'lead_magnet' | 'website' | 'any'
  destinationKind: 'homepage' | 'offer-page' | 'lead-magnet' | 'booking' | 'signup' | 'blog' | 'external'
  intent: 'subscribe' | 'download' | 'book-call' | 'trial' | 'buy' | 'reply' | 'share' | 'visit' | 'learn'
}

// ─── API ──────────────────────────────────────────────────────────────

export async function fetchKnowledge(workspaceId: string | number): Promise<KnowledgePayload> {
  const { data } = await api.get(`/private/workspaces/${workspaceId}/knowledge`)
  return data?.data || {}
}

/** One entity's state on the Overview page. `status` is the context engine's
 *  own verdict: 'good' | 'low_conf' | 'stale' | 'missing'. */
export interface KnowledgeEntityState {
  type: string
  status: 'good' | 'low_conf' | 'stale' | 'missing'
  present: boolean
  confidence: number | null
  updatedAt: string | null
}

export interface KnowledgeOverview {
  /** 0-100 completeness across the whole knowledge base. */
  overall: number
  byGroup: Record<string, { score: number; total: number; good: number }>
  entities: KnowledgeEntityState[]
  /** True when the Strategy document can be generated — the SAME rule the
   *  server's generate gate enforces (every section the document reads
   *  exists). Drives the Strategy menu lock. */
  strategyReady: boolean
  /** Generation in flight — per-entity step status while a foundation or
   *  strategy run is queued/working; null when nothing is running. */
  generating: {
    sessionId: string
    preset: string
    status: 'queued' | 'running'
    entities: Record<string, 'pending' | 'active' | 'done' | 'failed'>
  } | null
}

/** Start over — erases the whole Business Brain and re-mints the seed
 *  question. Irreversible; callers confirm with a warning dialog first. */
export async function startOverKnowledge(workspaceId: string | number): Promise<void> {
  await api.post(`/private/workspaces/${workspaceId}/knowledge/start-over`)
}

export async function fetchKnowledgeOverview(
  workspaceId: string | number,
): Promise<KnowledgeOverview> {
  const { data } = await api.get(`/private/workspaces/${workspaceId}/knowledge/overview`)
  return data
}

export async function saveKnowledge(
  workspaceId: string | number,
  type: KnowledgeType,
  content: unknown,
): Promise<KnowledgeEntry> {
  try {
    const { data } = await api.put(
      `/private/workspaces/${workspaceId}/knowledge/${encodeURIComponent(type)}`,
      { content },
    )
    return data?.data
  } catch (e: any) {
    // Surface the backend's validation message (e.g. "objections: expected 5–10 entries, got 4")
    // instead of the generic axios "Request failed with status code 400".
    const msg = e?.response?.data?.message
    if (msg) throw new Error(msg)
    throw e
  }
}

// ─── AI suggestions (create popups) ───────────────────────────────────
// On-demand candidate items for a Knowledge entity. Does NOT persist — the
// caller appends the picked items via saveKnowledge. Charges credits per call.

export async function suggestKnowledge(
  workspaceId: string | number,
  type: KnowledgeType,
): Promise<any[]> {
  try {
    const { data } = await api.post(
      `/private/workspaces/${workspaceId}/knowledge/${encodeURIComponent(type)}/suggest`,
    )
    return (data?.data?.items as any[]) || []
  } catch (e: any) {
    const msg = e?.response?.data?.message
    if (msg) throw new Error(msg)
    throw e
  }
}

// ─── Context questions (AI settings page) ─────────────────────────────

export interface ContextQuestion {
  id: string
  text: string
  kind: 'short_text' | 'long_text' | 'enum' | 'directive'
  examples?: string[]
  placeholder?: string
  options?: Array<{ value: string; label: string }>
}

export interface ContextQuestionsPayload {
  language: string
  questions: Partial<Record<KnowledgeType, ContextQuestion[]>>
}

export async function fetchContextQuestions(
  workspaceId: string | number,
): Promise<ContextQuestionsPayload> {
  const { data } = await api.get(
    `/private/workspaces/${workspaceId}/context-questions`,
  )
  return data?.data || { language: 'en', questions: {} }
}

